<script setup lang="ts">
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import OnlineIndicator from '@/components/ui/OnlineIndicator.vue'
import { useNavigation } from '@/composables/useNavigation'
import { useBreakpoint } from '@/composables/useBreakpoint'
import MobileBottomNav from './MobileBottomNav.vue'
import Header from './Header.vue'

const { userMenuItems } = useNavigation()

const layout = useBreakpoint()
</script>

<template>
  <div class="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col">
    <!-- Header fijo -->
    <Header
      :layout="layout"
      :user-menu-items="userMenuItems"
    />

    <div class="flex-1 flex overflow-hidden">
      <!--
        Aquí no hay barra lateral, y es a propósito.

        Antes se montaba `DesktopSidebar` con `visible=false`, y como el botón que
        la abría solo vivía en la cabecera, en escritorio no había forma de ponerla
        a `true`: el panel fijo nunca se veía. En tablet sí se abría, como cajón.
        Con el menú en la barra de abajo en móvil y tablet, y en los enlaces de la
        cabecera en escritorio, la barra lateral se queda sin uso en el layout real
        y se deja de montar.

        El componente sigue existiendo para `DesktopShell`/`TabletShell`, que son
        los shells alternativos; el layout de verdad es este.
      -->

      <!-- Contenido principal -->
      <main class="flex-1 overflow-y-auto transition-all duration-200 pt-16">
        <div class="container-crm py-6">
          <RouterView v-slot="{ Component }">
            <component :is="Component" />
          </RouterView>
        </div>
      </main>
    </div>

    <!--
      Navegación inferior en móvil y tablet. `MobileBottomNav` tiene raíz de
      fragmento (`nav` + `Teleport`), por lo que `v-show` avisaría en consola y no
      funcionaría; `<template v-if>` no genera nodo extra.

      En tablet estaba solo en móvil, con lo que la única navegación de una tablet
      vertical era un cajón lateral. Con el menú abajo, los dos tamaños se
      comportan igual y la cabecera no necesita un botón para abrir nada.
    -->
    <template v-if="layout !== 'desktop'">
      <MobileBottomNav :layout="layout" />
    </template>

    <!-- Indicador online/offline y cola de sincronización -->
    <OnlineIndicator />

    <!-- Toast y ConfirmDialog globales -->
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<style scoped>
/* Asegurar que el contenido no se tape */
@media (max-width: 767px) {
  :deep(.p-drawer) {
    width: 100%;
    max-width: 280px;
  }
}

/* Scroll suave */
:deep(main) {
  scroll-behavior: smooth;
}

/* Focus visible para accesibilidad */
:deep(button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible) {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}

/* ============================================================
   Espacio para la barra de navegación de abajo.

   Móvil y tablet llevan ya la barra inferior, que mide 64px
   (`h-16`) más el safe-area del notch. Antes tablet solo
   reservaba 40px porque no había barra: se reservaba para la
   barra de acciones del cotizador, que es `sticky` y ocupa su
   sitio en el flujo. Con la barra de navegación presente, 40px
   no bastan y el último renglón quedaba debajo de la barra.

   Escritorio no lleva barra inferior, así que no reserva nada.
   ============================================================ */
@media (max-width: 1023px) {
  :deep(main) {
    padding-bottom: 80px; /* barra de navegación de abajo + safe-area */
  }
}

@media (min-width: 1024px) {
  :deep(main) {
    padding-bottom: 0; /* escritorio: no hay barra inferior */
  }
}
</style>